import {
	UNDO_CLEAR_SCOPE,
	UNDO_MARK_TOAST_HIDDEN,
	UNDO_MARK_UNDONE,
	UNDO_REGISTER_COMMAND,
	UNDO_REMOVE_COMMAND,
	UNDO_STACK_MAX_DEPTH,
	UNDO_UPDATE_TOAST_ID,
} from "utils/undo/constants";

export const initialUndoHistoryState = {
	scopes: {},
};

function emptyScopeState() {
	return {
		commands: [],
		activeCommandId: null,
	};
}

function getScopeState(state, scope) {
	return state.scopes[scope] || emptyScopeState();
}

function findActiveCommandId(commands) {
	for (let index = commands.length - 1; index >= 0; index -= 1) {
		const command = commands[index];
		if (!command.undone && command.toastVisible) {
			return command.id;
		}
	}
	return null;
}

function trimCommands(commands) {
	if (commands.length <= UNDO_STACK_MAX_DEPTH) {
		return commands;
	}
	return commands.slice(commands.length - UNDO_STACK_MAX_DEPTH);
}

export default function undoHistoryReducer(state = initialUndoHistoryState, action) {
	switch (action.type) {
		case UNDO_REGISTER_COMMAND: {
			const { scope, command } = action.payload;
			const scopeState = getScopeState(state, scope);
			const commands = trimCommands([...scopeState.commands, command]);
			const activeCommandId = findActiveCommandId(commands);

			return {
				...state,
				scopes: {
					...state.scopes,
					[scope]: {
						commands,
						activeCommandId,
					},
				},
			};
		}
		case UNDO_MARK_UNDONE: {
			const { scope, commandId } = action.payload;
			const scopeState = getScopeState(state, scope);
			const commands = scopeState.commands.map((command) =>
				command.id === commandId ? { ...command, undone: true } : command
			);
			const activeCommandId = findActiveCommandId(commands);

			return {
				...state,
				scopes: {
					...state.scopes,
					[scope]: {
						commands,
						activeCommandId,
					},
				},
			};
		}
		case UNDO_MARK_TOAST_HIDDEN: {
			const { scope, commandId } = action.payload;
			const scopeState = getScopeState(state, scope);
			const commands = scopeState.commands.map((command) =>
				command.id === commandId
					? { ...command, toastVisible: false }
					: command
			);
			const activeCommandId = findActiveCommandId(commands);

			return {
				...state,
				scopes: {
					...state.scopes,
					[scope]: {
						commands,
						activeCommandId,
					},
				},
			};
		}
		case UNDO_UPDATE_TOAST_ID: {
			const { scope, commandId, toastId } = action.payload;
			const scopeState = getScopeState(state, scope);
			const commands = scopeState.commands.map((command) =>
				command.id === commandId ? { ...command, toastId } : command
			);

			return {
				...state,
				scopes: {
					...state.scopes,
					[scope]: {
						...scopeState,
						commands,
					},
				},
			};
		}
		case UNDO_REMOVE_COMMAND: {
			const { scope, commandId } = action.payload;
			const scopeState = getScopeState(state, scope);
			const commands = scopeState.commands.filter(
				(command) => command.id !== commandId
			);
			const activeCommandId = findActiveCommandId(commands);

			return {
				...state,
				scopes: {
					...state.scopes,
					[scope]: {
						commands,
						activeCommandId,
					},
				},
			};
		}
		case UNDO_CLEAR_SCOPE: {
			const { scope } = action.payload;
			if (!state.scopes[scope]) {
				return state;
			}

			const nextScopes = { ...state.scopes };
			delete nextScopes[scope];
			return {
				...state,
				scopes: nextScopes,
			};
		}
		default:
			return state;
	}
}

export function selectUndoScope(state, scope) {
	return state.undoHistoryReducer?.scopes?.[scope] || emptyScopeState();
}

export function selectActiveUndoCommand(state, scope) {
	const scopeState = selectUndoScope(state, scope);
	if (!scopeState.activeCommandId) {
		return null;
	}
	return (
		scopeState.commands.find(
			(command) => command.id === scopeState.activeCommandId
		) || null
	);
}

export function selectVisibleUndoCommands(state, scope) {
	const scopeState = selectUndoScope(state, scope);
	return scopeState.commands.filter(
		(command) => command.toastVisible && !command.undone
	);
}
